import React, { useEffect, useState } from "react";
import { Field, inputCls, PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import { FREQUENCIES } from "@/lib/api";
import { X, Plus, Trash2, Bell } from "lucide-react";

const ROLES = ["Administrador", "Conserje", "Comité", "Encargado", "Representante Legal", "Otro"];

const emptyContact = () => ({ name: "", email: "", phone: "", role: "", notify_before_visit: false });

export const CLASSIFICATIONS = [
  { value: "activo_contrato", label: "Activo con contrato", color: "#00C853" },
  { value: "ocasional",        label: "Ocasional",           color: "#1976D2" },
  { value: "prospecto",        label: "Prospecto",           color: "#FF5A00" },
  { value: "inactivo",         label: "Inactivo",            color: "#757575" },
];

export default function CustomerForm({ initial, onSubmit, onClose }) {
  const [data, setData] = useState({
    name: "",
    rut: "",
    address: "",
    commune: "",
    city: "Santiago",
    frequency: "monthly",
    price_uf: 0,
    contacts: [emptyContact()],
    notes: "",
    active: true,
    next_service_date: "",
    billing_day: 1,
    requires_oc: false,
    special_schedule: "",
    special_considerations: "",
    classification: "activo_contrato",
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      const merged = { ...data, ...initial };
      if (!merged.contacts || merged.contacts.length === 0) merged.contacts = [emptyContact()];
      setData(merged);
    }
    // eslint-disable-next-line
  }, [initial]);

  const change = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setData((d) => ({ ...d, [k]: v }));
  };

  const updateContact = (idx, key, value) => {
    const contacts = [...data.contacts];
    contacts[idx] = { ...contacts[idx], [key]: value };
    setData({ ...data, contacts });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...data,
        price_uf: Number(data.price_uf || 0),
        billing_day: Number(data.billing_day || 1),
        next_service_date: data.next_service_date || null,
        contacts: (data.contacts || []).filter((c) => (c.name || "").trim() || (c.email || "").trim()),
      };
      await onSubmit(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="customer-form-modal" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl border-t-4 border-[#FF5A00] max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Cliente</div>
            <div className="font-display font-bold text-xl">{initial ? "Editar cliente" : "Nuevo cliente"}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="customer-form-close" className="hover:bg-neutral-100 p-2">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <section>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">// Identificación</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Field label="Nombre de la comunidad / edificio *">
                  <input data-testid="customer-name-input" required value={data.name} onChange={change("name")} className={inputCls} placeholder="Ej: Edificio Alto Alessandri II" />
                </Field>
              </div>
              <Field label="RUT *">
                <input data-testid="customer-rut-input" required value={data.rut || ""} onChange={change("rut")} className={inputCls} placeholder="96.123.456-7" />
              </Field>
              <Field label="Próximo servicio">
                <input type="date" data-testid="customer-next-service-input" value={data.next_service_date || ""} onChange={change("next_service_date")} className={inputCls} />
              </Field>
            </div>
          </section>

          <section>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">// Ubicación</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <Field label="Dirección *">
                  <input data-testid="customer-address-input" required value={data.address} onChange={change("address")} className={inputCls} />
                </Field>
              </div>
              <Field label="Comuna">
                <input data-testid="customer-commune-input" value={data.commune || ""} onChange={change("commune")} className={inputCls} />
              </Field>
              <Field label="Ciudad">
                <input value={data.city} onChange={change("city")} className={inputCls} />
              </Field>
            </div>
          </section>

          <section>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">// Contrato</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Precio mensual (UF) *" hint="Se le suma IVA. Si va con OC, marca abajo.">
                <div className="relative">
                  <input type="number" step="0.01" min="0" required data-testid="customer-price-uf-input" value={data.price_uf} onChange={change("price_uf")} className={`${inputCls} pr-12`} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono-kpi text-xs text-neutral-500">UF</span>
                </div>
              </Field>
              <Field label="Periodicidad *">
                <select data-testid="customer-frequency-select" value={data.frequency} onChange={change("frequency")} className={inputCls}>
                  {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </Field>
              <Field label="Día de facturación" hint="Día del mes en que se factura este servicio.">
                <input type="number" min="1" max="31" data-testid="customer-billing-day-input" value={data.billing_day || 1} onChange={change("billing_day")} className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label="Clasificación *" hint="Cómo se relaciona con All Active. Define su tratamiento operacional.">
                <select data-testid="customer-classification-select" value={data.classification || "activo_contrato"} onChange={change("classification")} className={inputCls}>
                  {CLASSIFICATIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <label className="inline-flex items-center gap-2 cursor-pointer border border-neutral-300 px-3 py-2 bg-neutral-50">
                  <input type="checkbox" data-testid="customer-requires-oc" checked={!!data.requires_oc} onChange={change("requires_oc")} className="w-4 h-4" />
                  <span className="text-sm">Con <b>OC</b></span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer border border-neutral-300 px-3 py-2 bg-neutral-50">
                  <input type="checkbox" data-testid="customer-active-checkbox" checked={!!data.active} onChange={change("active")} className="w-4 h-4" />
                  <span className="text-sm">Activo</span>
                </label>
              </div>
            </div>
          </section>

          <section>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">// Consideraciones operativas</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Horarios especiales / restricciones de acceso">
                <textarea data-testid="customer-schedule-input" rows={2} value={data.special_schedule || ""} onChange={change("special_schedule")} className={inputCls} placeholder="Ej: Sólo lunes a viernes 9-18. Acceso por subterráneo 2." />
              </Field>
              <Field label="Otras consideraciones (avisos, accesos, llaves)">
                <textarea data-testid="customer-considerations-input" rows={2} value={data.special_considerations || ""} onChange={change("special_considerations")} className={inputCls} placeholder="Ej: Avisar a Sra. Marta antes de subir, retirar llave en conserjería." />
              </Field>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">// Contactos</div>
              <button
                type="button"
                data-testid="customer-add-contact"
                onClick={() => setData({ ...data, contacts: [...data.contacts, emptyContact()] })}
                className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-[#FF5A00] hover:bg-[#FF5A00]/5 px-2 py-1 border border-[#FF5A00]/30"
              >
                <Plus className="w-3 h-3 inline -mt-0.5 mr-1" />Agregar contacto
              </button>
            </div>
            <div className="space-y-3">
              {data.contacts.map((c, i) => (
                <div key={`contact-${i}-${c.email || c.name || "draft"}`} data-testid={`customer-contact-${i}`} className="border border-neutral-200 p-3 bg-neutral-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Contacto #{i + 1}</div>
                    {data.contacts.length > 1 && (
                      <button type="button" onClick={() => setData({ ...data, contacts: data.contacts.filter((_, idx) => idx !== i) })} className="text-[#D50000] hover:bg-[#D50000]/10 p-1">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Email *">
                      <input type="email" required data-testid={`customer-contact-email-${i}`} value={c.email || ""} onChange={(e) => updateContact(i, "email", e.target.value)} className={inputCls} placeholder="contacto@cliente.cl" />
                    </Field>
                    <Field label="Nombre">
                      <input data-testid={`customer-contact-name-${i}`} value={c.name || ""} onChange={(e) => updateContact(i, "name", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Teléfono">
                      <input value={c.phone || ""} onChange={(e) => updateContact(i, "phone", e.target.value)} className={inputCls} placeholder="+56 9 …" />
                    </Field>
                    <Field label="Rol / cargo">
                      <select value={c.role || ""} onChange={(e) => updateContact(i, "role", e.target.value)} className={inputCls}>
                        <option value="">— Sin rol —</option>
                        {ROLES.map((r) => <option key={r}>{r}</option>)}
                      </select>
                    </Field>
                    <label className="inline-flex items-center gap-2 mt-6 text-sm cursor-pointer md:col-span-2">
                      <input type="checkbox" data-testid={`customer-contact-notify-${i}`} checked={!!c.notify_before_visit} onChange={(e) => updateContact(i, "notify_before_visit", e.target.checked)} className="w-4 h-4" />
                      <Bell className="w-3.5 h-3.5 text-[#FF5A00]" />
                      <span>Avisar 24h antes del servicio</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <Field label="Notas internas">
              <textarea data-testid="customer-notes-input" rows={3} value={data.notes} onChange={change("notes")} className={inputCls} />
            </Field>
          </section>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50 sticky bottom-0">
          <SecondaryButton type="button" onClick={onClose} data-testid="customer-form-cancel">Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={saving} data-testid="customer-form-submit">
            {saving ? "Guardando…" : initial ? "Guardar cambios" : "Crear cliente + contrato"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}
